import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { BarCode, ArrowRight2 } from "../CustomComponents/SwiftIcon/Icons";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import Pulse from "../Loader/Pulse";
import {
  checkLength,
  isEmpty,
  isValidEmail,
  isValidPan,
  isValidPhoneNumber,
} from "../../exports/UtilFunctions";
import ServerRequest from "../../exports/ServerRequest";
import AccessHeader from "./AccessHeader";
import { COLOR_VARS } from "../../exports/ColorVars";

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

function AccessSignupNew() {
  const [step, setStep] = useState(1);

  const [email, setEmail] = useState("");
  const [otp, setOTP] = useState("");
  const [number, setNumber] = useState("");
  const [pan, setPan] = useState("");

  const [timer, setTimer] = useState(0);

  const [Error1, setError1] = useState("");
  const [Error2, setError2] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);

  const [otpSent, setOTPSent] = useState(false);

  const [noPan, setNoPan] = useState(false);
  const [incorrectPan, setIncorrectPan] = useState(false);

  const [panDetails, setPanDetails] = useState(null);

  const handleNext = () => {
    setStep(step + 1);
  };

  useEffect(() => {
    const counter = timer > 0 && setInterval(() => setTimer(timer - 1), 1000);
    return () => clearInterval(counter);
  }, [timer]);

  async function sendOTP() {
    if (timer > 0) {
      return;
    }

    let isValid = false;
    let errorMessage = "";

    let data = false;

    if (step === 2) {
      if (isEmpty(email) || !isValidEmail(email)) {
        errorMessage = "Please enter a valid email address";
        isValid = false;
      } else {
        setLoading(true);
        setLoadingMessage("Sending one-time password...");

        data = await ServerRequest({
          method: "POST",
          url: `/access/signup/sendotp`,
          data: {
            type: "email",
            value: email,
          },
        });
      }
    } else if (step === 3) {
      if (isEmpty(number) || !isValidPhoneNumber(number)) {
        errorMessage = "Please enter a valid phone number";
        isValid = false;
      } else {
        setLoading(true);
        setLoadingMessage("Sending one-time password...");
        data = await ServerRequest({
          method: "POST",
          url: `/access/signup/sendotp`,
          data: {
            type: "mobile",
            value: number,
          },
        });
      }
    }

    if (errorMessage || data?.error) {
      errorMessage =
        errorMessage ||
        data?.message ||
        "Failed to send OTP. Please try again.";
      isValid = false;
    } else {
      isValid = true;
    }

    if (!isValid) {
      Alert({
        TitleText: "Warning",
        Message: errorMessage,
        ...warning_config,
      });
      setLoading(false);
      setLoadingMessage("");
      setError1("");
      setError2("");
    } else {
      setTimeout(() => {
        setError1("");
        setError2("");
        setLoading(false);
        setLoadingMessage("");

        if (step === 4) {
          setStep(5);
        } else {
          setOTPSent(true);
          setTimer(30);
        }
      }, 2000);
    }
  }

  async function validateOtp() {
    if (isEmpty(otp) || !checkLength(otp, 6)) {
      setError1("");
      setError2("Please enter correct otp");
    } else {
      setLoading(true);
      setLoadingMessage("Validating one time password...");

      const data = await ServerRequest({
        url: `/access/signup/validateotp`,
        data: {
          type: step === 2 ? "email" : "number",
          value: step === 2 ? email : number,
          otp: otp,
        },
        method: "post",
      });

      if (!data.error) {
        setTimeout(() => {
          setTimer(0);
          setError1("");
          setError2("");
          setLoading(false);
          setLoadingMessage(false);
          setOTPSent(false);
          setOTP("");
          if (step === 3) {
            handleSubmit();
          } else {
            handleNext();
          }
        }, 2000);
      } else {
        setTimeout(() => {
          setError1("");
          setError2(data.message);
          setLoading(false);
          setLoadingMessage("");
        }, 1000);
      }
    }
  }

  function KeyUp1(e) {
    if (e.key === "Enter") {
      sendOTP();
    }
  }

  function KeyUp2(e) {
    if (e.key === "Enter") {
      validateOtp();
    }
  }

  async function handleSubmit() {
    if (!panDetails) return;

    const pan = panDetails?.pan_number;
    const name = panDetails?.user_full_name || "";

    const myForm = new FormData();
    myForm.append("pan", pan);
    myForm.append("name", name);
    myForm.append("email", email);
    myForm.append("mobile", number);

    const user_address = panDetails?.user_address || {};

    const address = {
      address_line_1: user_address?.line_1,
      address_line_2: user_address?.line_2,
      address_line_3: user_address?.street_name,
      city: user_address?.city,
      state: user_address?.state,
      country: user_address?.country,
      pincode: user_address?.zip,
      full: user_address?.full,
    };

    const payload = {
      pan: pan,
      name: name,
      email: email,
      mobile: number,
      address: address,
    };

    // console.log(myForm)
    setLoading(true);
    setLoadingMessage("Creating account, please wait...");

    const data = await ServerRequest({
      method: "POST",
      url: "/access/signup/account",
      data: payload,
      headers: {
        "Content-type": "application/json",
      },
    });

    if (data.error) {
      Alert({
        TitleText: "Warning",
        Message: data?.message,
        ...warning_config,
      });
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    setLoading(false);
    setLoadingMessage("");
    handleNext();
  }

  async function GetPanDetails() {
    if (isEmpty(pan) || !isValidPan(pan)) {
      setError1("");
      setError2("Please enter valid PAN number");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Fetching PAN details...");
      setError1("");
      setError2("");
      const data = await ServerRequest({
        method: "POST",
        url: `/access/signup/validatepan`,
        data: {
          type: "pan",
          value: pan,
        },
      });
      if (data?.error) {
        throw new Error(data?.message || "Failed to fetch PAN details");
      } else {
        const panData = await ServerRequest({
          method: "GET",
          url: `/access/signup/getpan?pan=${pan}`,
        });
        if (!panData?.success) {
          throw new Error(panData?.message || "Failed to fetch PAN details");
        } else if (!panData?.result) {
          throw new Error(
            "Failed to fetch PAN details, Please enter a valid PAN",
          );
        } else {
          setPanDetails(panData?.result);
        }
      }
    } catch (error) {
      Alert({
        TitleText: "Warning",
        Message: error.message,
        ...warning_config,
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  }

  if (step === 4) {
    return <Navigate to="/access/success" />;
  }

  return (
    <div className="user-login">
      <div className="user-login-popup-wrapper">
        {loading ? (
          <>
            <div className="popup-loader">
              <Pulse />
              {/* <p>{loadingMessage}</p> */}
            </div>
          </>
        ) : (
          <>
            <div className="login-header">
              <AccessHeader header_text="SignUp" />
            </div>

            {step === 1 && (
              <>
                <div className="login-content">
                  {noPan ? (
                    <>
                      <div className="login-body">
                        <div className="login-notes">
                          <p className="login-note">
                            We only support digital onboarding for valid Aadhaar
                            linked PANs. Please reach out to&nbsp;
                            <strong>hello@swiftfolios.com</strong> or WhatsApp
                            on <strong>+91 8160080658</strong>&nbsp;and we will
                            be happy to assist you in opening an account.
                          </p>
                        </div>
                        <p
                          onClick={() => setNoPan(false)}
                          className="swift-pan-text"
                        >
                          Go Back
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      {panDetails ? (
                        <>
                          {incorrectPan ? (
                            <>
                              <div className="login-body">
                                <div className="login-notes">
                                  <p className="login-note">
                                    We kindly request you to go to{" "}
                                    <a
                                      className="login-link"
                                      target="_blank"
                                      href="https://www.onlineservices.nsdl.com/paam/endUserRegisterContact.html"
                                    >
                                      NSDL Online PAN Card Services - Apply For
                                      New PAN Card & Corrections
                                    </a>{" "}
                                    and correct your address according to your
                                    Aadhaar. It is an online process and will
                                    take only 5 minutes.
                                  </p>
                                  <p className="login-note">
                                    Post updation of your address on your PAN,
                                    you may resume your sign-up process. This is
                                    important as your PAN Data and Aadhaar data
                                    need to be consistent as per Government
                                    Regulations.
                                  </p>
                                  <p className="login-note">
                                    For any query or if you want us to process
                                    this change, please reach out to &nbsp;
                                    <strong>hello@swiftfolios.com</strong> or
                                    WhatsApp on <strong>+91 8160080658</strong>{" "}
                                    and we will be happy to assist you. Charges
                                    of Rs. 72 from NSDL will apply
                                  </p>
                                </div>
                                <p
                                  onClick={() => setIncorrectPan(false)}
                                  className="user-incorrect-details"
                                >
                                  Go Back
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="login-body">
                                <div className="user-data">
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">
                                      Full Name
                                    </p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_full_name || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">
                                      Masked Aadhaar
                                    </p>
                                    <p className="swift-data-value">
                                      {panDetails?.masked_aadhaar || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">Address</p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_address?.full || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">Gender</p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_gender || "-"}
                                    </p>
                                  </div>
                                  <div className="swift-data-box">
                                    <p className="swift-data-label">DOB</p>
                                    <p className="swift-data-value">
                                      {panDetails?.user_dob || "-"}
                                    </p>
                                  </div>
                                </div>
                                <p
                                  className="user-incorrect-details"
                                  onClick={() => setIncorrectPan(true)}
                                >
                                  Incorrect details?
                                </p>
                              </div>
                              <div className="login-footer">
                                <div
                                  className="swift-button-icon btn-xl"
                                  onClick={handleNext}
                                >
                                  <p>Proceed</p>
                                  {/* <div className="button-icon">
                                    <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                                  </div> */}
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="login-body">
                            <div className="swift-input-box">
                              <p>PAN Number</p>
                              <input
                                className="swift-input"
                                type="text"
                                maxLength={10}
                                value={pan}
                                onChange={(e) => {
                                  setPan(e.target.value.toUpperCase());
                                }}
                                onKeyUp={KeyUp1}
                                placeholder=""
                              />
                              <p
                                className={
                                  Error2 === ""
                                    ? "error-text hide"
                                    : "error-text"
                                }
                              >
                                {Error2}
                              </p>
                            </div>
                            <p
                              onClick={() => setNoPan(true)}
                              className="swift-pan-text"
                            >
                              I don't have a PAN?
                            </p>
                          </div>
                          <div className="login-footer">
                            <div
                              className="swift-button-icon btn-xl"
                              onClick={GetPanDetails}
                            >
                              <p>Next</p>
                              {/* <div className="button-icon">
                                <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                              </div> */}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Email</p>
                      <input
                        className="swift-input"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                        }}
                        onKeyUp={KeyUp1}
                        placeholder="Your email address"
                      />
                      <p
                        className={
                          Error1 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {Error1}
                      </p>
                    </div>
                    <div className="swift-button-icon" onClick={sendOTP}>
                      {/* <div className="button-icon">
                        <BarCode size={20} color={COLOR_VARS["SWIFT_COLOR8"]} />
                      </div> */}
                      <p>Generate OTP</p>
                    </div>
                    {otpSent && (
                      <>
                        <p
                          className={
                            timer === 0
                              ? "resend-message hide"
                              : "resend-message"
                          }
                        >
                          Please wait {timer} seconds to resend
                        </p>
                        <div className="swift-input-box">
                          <p>OTP</p>
                          <input
                            className="swift-input"
                            value={otp}
                            placeholder="123456"
                            onKeyUp={KeyUp2}
                            maxLength={6}
                            onChange={(e) => {
                              setOTP(e.target.value.replace(/[^\d]/g, ""));
                            }}
                          />
                          <p
                            className={
                              Error2 === "" ? "error-text hide" : "error-text"
                            }
                          >
                            {Error2}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {otpSent && (
                    <>
                      <div className="login-footer">
                        <div
                          className="swift-button-icon btn-xl"
                          onClick={validateOtp}
                        >
                          <p>Next</p>
                          {/* <div className="button-icon">
                            <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                          </div> */}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Mobile Number</p>
                      <input
                        className="swift-input"
                        maxLength={10}
                        type="text"
                        value={number}
                        onChange={(e) => {
                          setNumber(e.target.value.replace(/[^\d]/g, ""));
                        }}
                        onKeyUp={KeyUp1}
                        placeholder="Your mobile number"
                      />
                      <p
                        className={
                          Error1 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {Error1}
                      </p>
                    </div>
                    <div className="swift-button-icon" onClick={sendOTP}>
                      {/* <div className="button-icon">
                        <BarCode size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />
                      </div> */}
                      <p>Generate OTP</p>
                    </div>
                    {otpSent && (
                      <>
                        <p
                          className={
                            timer === 0
                              ? "resend-message hide"
                              : "resend-message"
                          }
                        >
                          Please wait {timer} seconds to resend
                        </p>
                        <div className="swift-input-box">
                          <p>OTP</p>
                          <input
                            className="swift-input"
                            value={otp}
                            placeholder="123456"
                            onKeyUp={KeyUp2}
                            maxLength={6}
                            onChange={(e) => {
                              setOTP(e.target.value.replace(/[^\d]/g, ""));
                            }}
                          />
                          <p
                            className={
                              Error2 === "" ? "error-text hide" : "error-text"
                            }
                          >
                            {Error2}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {otpSent && (
                    <>
                      <div className="login-footer">
                        <div
                          className="swift-button-icon btn-xl"
                          onClick={validateOtp}
                        >
                          <p>Next</p>
                          {/* <div className="button-icon">
                            <ArrowRight2 size={16} color={COLOR_VARS["SWIFT_COLOR6"]} />
                          </div> */}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AccessSignupNew;
